import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MenuItem, TextField } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as XLSX from "xlsx";
import { BookingContentName, BookingContentWrap, MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import {
	CountryRatesPayload,
	getCountryOptions,
	getCountryRates,
	saveCountryRates,
} from "../../../services/CountryRatesService";
import { getSystemConfig } from "../../../services/SystemConfigService";
import { normalizeCountryKey, toCanonicalCountryName } from "./countryCatalog";
import { getMasterCurrencyCodes } from "./masterCurrencyCodes";
import PermissionGate from "../../controls/auth/PermissionGate";

type PricingCurrency = "USD" | "EUR";

type CurrencyOption = {
	code: string;
	name: string;
	label: string;
	searchText: string;
};

type CountryRatesRow = {
	country?: string;
	code?: string;
	currency?: string;
	wiseRate?: number | null;
	pubRate?: number | null;
	bouncePriceValue?: number | null;
	bouncePriceCurrency?: string;
	stationCom?: number | null;
	minimumPayout?: number | null;
	estMinRupCap?: number | null;
	minPayingCap?: number | null;
	estMaxRupCap?: number | null;
	maxPayingCap?: number | null;
};

type EstPubRateValue = number | "NEW" | null;

const COUNTRY_RATE_SNAPSHOT_DEFAULTS: Array<
	Pick<CountryRatesRow, "country" | "code" | "currency" | "wiseRate" | "bouncePriceValue" | "bouncePriceCurrency">
> = [
	{
		country: "Vietnam",
		code: "VND",
		currency: "Dong",
		wiseRate: 26266.3,
		bouncePriceValue: 5.45,
		bouncePriceCurrency: "USD",
	},
	{
		country: "Thailand",
		code: "THB",
		currency: "Baht",
		wiseRate: 31.39,
		bouncePriceValue: 205,
		bouncePriceCurrency: "THB",
	},
	{
		country: "Philippines",
		code: "PHP",
		currency: "Peso",
		wiseRate: 59.263,
		bouncePriceValue: 5.45,
		bouncePriceCurrency: "USD",
	},
	{
		country: "Malaysia",
		code: "MYR",
		currency: "Ringgit",
		wiseRate: 4.073,
		bouncePriceValue: 25.5,
		bouncePriceCurrency: "MYR",
	},
	{
		country: "Indonesia",
		code: "IDR",
		currency: "Rupiah",
		wiseRate: 16843,
		bouncePriceValue: 5.45,
		bouncePriceCurrency: "USD",
	},
	{
		country: "Laos",
		code: "LAK",
		currency: "Kip",
		wiseRate: 21608.5,
		bouncePriceValue: 5.45,
		bouncePriceCurrency: "USD",
	},
	{
		country: "Cambodia",
		code: "KHR",
		currency: "Riel",
		wiseRate: 4021,
		bouncePriceValue: 6.25,
		bouncePriceCurrency: "USD",
	},
	{
		country: "India",
		code: "INR",
		currency: "Rupee",
		wiseRate: 90.243,
		bouncePriceValue: 5.45,
		bouncePriceCurrency: "USD",
	},
	{
		country: "Nepal",
		code: "NPR",
		currency: "Rupee",
		wiseRate: 144.27,
		bouncePriceValue: 5.85,
		bouncePriceCurrency: "USD",
	},
	{
		country: "Mongolia",
		code: "MNT",
		currency: "Tugrik",
		wiseRate: 3554,
		bouncePriceValue: null,
		bouncePriceCurrency: "NONE",
	},
	{
		country: "South Africa",
		code: "ZAR",
		currency: "Rand",
		wiseRate: 16.492,
		bouncePriceValue: 125,
		bouncePriceCurrency: "ZAR",
	},
	{
		country: "Spain",
		code: "EUR",
		currency: "Euro",
		wiseRate: 0.859,
		bouncePriceValue: 6.7,
		bouncePriceCurrency: "EUR",
	},
	{
		country: "Turkey",
		code: "TRY",
		currency: "Lira",
		wiseRate: 43.01,
		bouncePriceValue: 6.7,
		bouncePriceCurrency: "EUR",
	},
	{
		country: "Hungary",
		code: "HUF",
		currency: "Forint",
		wiseRate: 331.401,
		bouncePriceValue: 6.7,
		bouncePriceCurrency: "EUR",
	},
	{
		country: "Singapore",
		code: "SGD",
		currency: "Dollar",
		wiseRate: 1.287,
		bouncePriceValue: 11,
		bouncePriceCurrency: "SGD",
	},
	{
		country: "Czechia",
		code: "CZK",
		currency: "Koruna",
		wiseRate: 20.867,
		bouncePriceValue: 7.7,
		bouncePriceCurrency: "EUR",
	},
	{
		country: "Mexico",
		code: "MXN",
		currency: "Peso",
		wiseRate: 17.977,
		bouncePriceValue: 120,
		bouncePriceCurrency: "MXN",
	},
	{
		country: "Egypt",
		code: "EGP",
		currency: "Pound",
		wiseRate: 47.23,
		bouncePriceValue: 275,
		bouncePriceCurrency: "EGP",
	},
];

const COUNTRY_RATE_SNAPSHOT_BY_CODE = new Map(
	COUNTRY_RATE_SNAPSHOT_DEFAULTS.map((row) => [String(row.code || "").toUpperCase(), row])
);

// Based on Excel snapshot: Est STT Com ~= WiseRate * 2.25 (converted to local)
const DEFAULT_STT_USD_AMOUNT = 2.25;

function parseNumberLoose(value: any): number | null {
	if (value === null || value === undefined) return null;
	if (typeof value === "number" && Number.isFinite(value)) return value;
	const s = String(value).trim();
	if (!s) return null;

	// Accept formats like: 26,269.000 (en-US) or 26.269,000 (legacy vi-VN)
	const hasComma = s.includes(",");
	const hasDot = s.includes(".");
	let normalized = s;

	if (hasComma && hasDot) {
		// Decide decimal separator by last occurrence
		const lastComma = s.lastIndexOf(",");
		const lastDot = s.lastIndexOf(".");
		const decimalSep = lastComma > lastDot ? "," : ".";
		const thousandSep = decimalSep === "," ? "." : ",";
		normalized = s.split(thousandSep).join("").replace(decimalSep, ".");
	} else if (hasComma && !hasDot) {
		// Treat comma as decimal separator
		normalized = s.replace(/\./g, "").replace(/,/g, ".");
	} else {
		// Only dot or neither
		// If it's a pure legacy vi-VN thousand-grouped number like 50.000 or 1.234.567, treat dots as thousand separators.
		const looksLikeThousandGrouped = /^-?\d{1,3}(\.\d{3})+$/.test(s);
		if (looksLikeThousandGrouped) {
			normalized = s.replace(/\./g, "");
		} else {
			normalized = s.replace(/,/g, "");
		}
	}

	const n = Number(normalized);
	return Number.isFinite(n) ? n : null;
}

function parseCanonicalViVNToNumber(canonical: string): number | null {
	if (!canonical) return null;
	// canonical format: optional '-', digits, optional '.' fractional digits; NO thousand separators.
	const n = Number(canonical);
	return Number.isFinite(n) ? n : null;
}

function roundTo(value: number, decimals: number) {
	const factor = Math.pow(10, decimals);
	return Math.round(value * factor) / factor;
}

function formatNumber(value: number | null | undefined, maxFractionDigits = 3) {
	if (value === null || value === undefined || !Number.isFinite(value)) return "";
	return new Intl.NumberFormat("en-US", {
		maximumFractionDigits: maxFractionDigits,
		minimumFractionDigits: 0,
	}).format(value);
}

function formatNumberFixed(value: number | null | undefined, fractionDigits: number) {
	if (value === null || value === undefined || !Number.isFinite(value)) return "";
	return new Intl.NumberFormat("en-US", {
		maximumFractionDigits: fractionDigits,
		minimumFractionDigits: fractionDigits,
	}).format(value);
}

function formatPercent(value: number | null | undefined, fractionDigits = 2) {
	if (value === null || value === undefined || !Number.isFinite(value)) return "";
	return new Intl.NumberFormat("en-US", {
		style: "percent",
		maximumFractionDigits: fractionDigits,
		minimumFractionDigits: fractionDigits,
	}).format(value);
}

function placeholderForDecimals(decimals: number) {
	if (decimals === 0) return "VD: 50,000";
	if (decimals === 2) return "VD: 5.45";
	return "VD: 26,269.000";
}

function sanitizeNumericText(input: string) {
	// Allow only digits, thousand/decimal separators (.,), optional leading minus.
	let s = input.replace(/[^0-9,\.\-\s]/g, "");
	// Keep only first minus and force it to be leading
	const hasMinus = s.includes("-");
	s = s.replace(/-/g, "");
	if (hasMinus) s = "-" + s;
	// Remove spaces
	s = s.replace(/\s+/g, "");
	return s;
}

function isAllowedNumericKey(key: string) {
	return /^[0-9.,-]$/.test(key);
}

function detectDecimalSeparator(s: string, decimals: number): "," | "." | null {
	if (decimals <= 0) return null;
	// Country Rates now uses a strict US-style numeric format:
	// ',' for thousands grouping and '.' for decimals.
	if (s.includes(".")) return ".";
	return null;
}

function canonicalizeNumericText(sanitized: string, decimals: number, decimalSep: "," | "." | null): string {
	if (!sanitized) return "";
	const neg = sanitized.startsWith("-");
	let s = sanitized.replace(/-/g, "");
	if (decimals <= 0 || !decimalSep) {
		const intDigits = s.replace(/[.,]/g, "");
		return (neg ? "-" : "") + intDigits;
	}

	const idx = s.lastIndexOf(decimalSep);
	if (idx < 0) {
		const intDigits = s.replace(/[.,]/g, "");
		return (neg ? "-" : "") + intDigits;
	}

	const intPart = s.slice(0, idx).replace(/[.,]/g, "");
	let fracPart = s.slice(idx + 1).replace(/[.,]/g, "");
	fracPart = fracPart.slice(0, Math.max(0, decimals));
	return (neg ? "-" : "") + intPart + "." + fracPart;
}

function addThousandsSeparatorsViVN(intDigits: string) {
	const digits = intDigits.replace(/^0+(?=\d)/, "");
	if (!digits) return "0";
	let out = "";
	let count = 0;
	for (let i = digits.length - 1; i >= 0; i--) {
		out = digits[i] + out;
		count++;
		if (count === 3 && i !== 0) {
			out = "," + out;
			count = 0;
		}
	}
	return out;
}

function formatCanonicalViVN(canonical: string) {
	if (!canonical) return "";
	const neg = canonical.startsWith("-");
	const body = neg ? canonical.slice(1) : canonical;
	const dotIdx = body.indexOf(".");
	const intDigits = dotIdx >= 0 ? body.slice(0, dotIdx) : body;
	const frac = dotIdx >= 0 ? body.slice(dotIdx + 1) : null;
	const groupedInt = addThousandsSeparatorsViVN(intDigits || "0");
	if (frac === null) return (neg ? "-" : "") + groupedInt;
	return (neg ? "-" : "") + groupedInt + "." + frac;
}

function mapCanonicalIndexToDisplayIndex(display: string, canonicalIndex: number) {
	if (canonicalIndex <= 0) return 0;
	let cPos = 0;
	for (let i = 0; i < display.length; i++) {
		if (cPos >= canonicalIndex) return i;
		const ch = display[i];
		if (ch === ",") continue; // thousand separators are not in canonical
		cPos++;
	}
	return display.length;
}

function FormattedNumberField(props: {
	value: number | null | undefined;
	decimals: number;
	onValueChange: (value: number | null) => void;
	disabled?: boolean;
}) {
	const { value, decimals, onValueChange, disabled } = props;
	const [text, setText] = useState<string>(
		value === null || value === undefined ? "" : formatNumberFixed(value, decimals)
	);
	const isFocusedRef = useRef(false);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const pendingCaretRef = useRef<number | null>(null);

	useEffect(() => {
		if (isFocusedRef.current) return;
		setText(value === null || value === undefined ? "" : formatNumberFixed(value, decimals));
	}, [value, decimals]);

	const onChange = (nextRaw: string, caretPos: number | null | undefined) => {
		const nextSanitized = sanitizeNumericText(nextRaw);
		if (nextSanitized.trim() === "") {
			pendingCaretRef.current = 0;
			setText("");
			onValueChange(null);
			return;
		}

		const decimalSep = detectDecimalSeparator(nextSanitized, decimals);
		const canonical = canonicalizeNumericText(nextSanitized, decimals, decimalSep);
		const display = formatCanonicalViVN(canonical);

		const safeCaretPos = caretPos === null || caretPos === undefined ? nextRaw.length : caretPos;
		const beforeRaw = nextRaw.slice(0, safeCaretPos);
		const beforeSanitized = sanitizeNumericText(beforeRaw);
		const canonicalBefore = canonicalizeNumericText(beforeSanitized, decimals, decimalSep);
		const desiredCaret = mapCanonicalIndexToDisplayIndex(display, canonicalBefore.length);
		pendingCaretRef.current = desiredCaret;

		setText(display);
		const parsed = parseCanonicalViVNToNumber(canonical);
		if (parsed !== null) onValueChange(parsed);
	};

	useLayoutEffect(() => {
		if (!isFocusedRef.current) return;
		const caret = pendingCaretRef.current;
		if (caret === null) return;
		pendingCaretRef.current = null;
		const el = inputRef.current;
		if (!el) return;
		try {
			el.setSelectionRange(caret, caret);
		} catch (e) {
			// ignore
		}
	}, [text]);

	const onBlur = () => {
		isFocusedRef.current = false;
		if (text.trim() === "") {
			setText("");
			onValueChange(null);
			return;
		}
		const sanitized = sanitizeNumericText(text);
		const decimalSep = detectDecimalSeparator(sanitized, decimals);
		const canonical = canonicalizeNumericText(sanitized, decimals, decimalSep);
		const parsed = parseCanonicalViVNToNumber(canonical);
		if (parsed === null) return;
		setText(formatNumberFixed(parsed, decimals));
		onValueChange(parsed);
	};

	return (
		<TextField
			type="text"
			value={text}
			onFocus={() => {
				isFocusedRef.current = true;
			}}
			inputRef={inputRef}
			onKeyDown={(e) => {
				// Block letters/symbols; allow navigation, shortcuts, and separators.
				if (e.ctrlKey || e.metaKey || e.altKey) return;
				const key = e.key;
				const allowedNonCharKeys = [
					"Backspace",
					"Delete",
					"ArrowLeft",
					"ArrowRight",
					"ArrowUp",
					"ArrowDown",
					"Home",
					"End",
					"Tab",
					"Enter",
				];
				if (allowedNonCharKeys.includes(key)) return;
				// Integer fields (decimals=0) do not allow decimal separator '.'
				if (decimals <= 0 && key === ".") {
					e.preventDefault();
					return;
				}
				if (key.length === 1 && !isAllowedNumericKey(key)) {
					e.preventDefault();
				}
			}}
			onPaste={(e) => {
				const paste = e.clipboardData?.getData("text") ?? "";
				const sanitized = sanitizeNumericText(paste);
				e.preventDefault();
				const el = inputRef.current;
				const start = el?.selectionStart ?? text.length;
				const end = el?.selectionEnd ?? text.length;
				const nextRaw = (text.slice(0, start) + sanitized + text.slice(end)).slice(0, 100);
				const nextCaret = start + sanitized.length;
				onChange(nextRaw, nextCaret);
			}}
			onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
			onBlur={onBlur}
			variant="outlined"
			size="small"
			disabled={disabled}
			placeholder={placeholderForDecimals(decimals)}
			inputProps={{
				inputMode: "decimal",
				pattern: "^-?[0-9.,]*$",
				title:
					decimals <= 0
						? "Chỉ nhập số nguyên. Hệ thống tự tách phần ngàn bằng dấu , (VD: 50,000)."
						: "Nhập số theo định dạng US: dấu , là phần ngàn, dấu . là thập phân (VD: 26,269.000)",
			}}
			fullWidth
		/>
	);
}

function getPricingCurrency(code?: string): PricingCurrency {
	return String(code || "").toUpperCase() === "EUR" ? "EUR" : "USD";
}

function getAllCurrencyCodes(): string[] {
	try {
		const anyIntl = Intl as any;
		if (anyIntl && typeof anyIntl.supportedValuesOf === "function") {
			const values = anyIntl.supportedValuesOf("currency");
			if (Array.isArray(values) && values.length) return values;
		}
	} catch (e) {
		// ignore
	}
	return getMasterCurrencyCodes();
}

function getCurrencyDisplayName(code: string) {
	try {
		const anyIntl = Intl as any;
		if (anyIntl && typeof anyIntl.DisplayNames === "function") {
			const dn = new anyIntl.DisplayNames(["vi-VN"], { type: "currency" });
			return dn.of(code) || "";
		}
	} catch (e) {
		// ignore
	}
	return "";
}

function findCurrencyOptionByInput(input: string, options: CurrencyOption[]): CurrencyOption | null {
	const normalized = String(input || "")
		.trim()
		.toLowerCase();
	if (!normalized) return null;

	return (
		options.find(
			(option) => option.code.toLowerCase() === normalized || option.name.toLowerCase() === normalized
		) || null
	);
}

function uniqUpper(items: string[]) {
	const out: string[] = [];
	const seen = new Set<string>();
	for (const raw of items) {
		const v = String(raw || "")
			.trim()
			.toUpperCase();
		if (!v) continue;
		if (seen.has(v)) continue;
		seen.add(v);
		out.push(v);
	}
	return out;
}

function uniqCountries(items: Array<string | null | undefined>) {
	const out: string[] = [];
	const seen = new Set<string>();
	for (const raw of items) {
		const v = String(raw ?? "").trim();
		if (!v) continue;
		const key = v.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(v);
	}
	out.sort((a, b) => a.localeCompare(b));
	return out;
}

function shouldUseSnapshotTextFallback(value: string | null | undefined) {
	return value === null || value === undefined || String(value).trim() === "";
}

function shouldUseSnapshotNumberFallback(value: number | null | undefined) {
	return value === null || value === undefined || !Number.isFinite(value);
}

function applyCountryRateSnapshotFallback(row: CountryRatesRow): CountryRatesRow {
	const codeKey = String(row.code || "")
		.trim()
		.toUpperCase();
	const snapshotByCode = codeKey ? COUNTRY_RATE_SNAPSHOT_BY_CODE.get(codeKey) : null;
	const countryKey = normalizeCountryKey(row.country || "");
	const snapshotByCountry =
		!snapshotByCode && countryKey
			? COUNTRY_RATE_SNAPSHOT_DEFAULTS.find(
					(snapshot) => normalizeCountryKey(snapshot.country || "") === countryKey
			  ) || null
			: null;
	const snapshot = snapshotByCode || snapshotByCountry;
	if (!snapshot) return row;

	return {
		...row,
		country: shouldUseSnapshotTextFallback(row.country) ? snapshot.country : row.country,
		code: shouldUseSnapshotTextFallback(row.code) ? snapshot.code : row.code,
		currency: shouldUseSnapshotTextFallback(row.currency) ? snapshot.currency : row.currency,
		wiseRate: shouldUseSnapshotNumberFallback(row.wiseRate) ? snapshot.wiseRate ?? null : row.wiseRate,
		bouncePriceValue: shouldUseSnapshotNumberFallback(row.bouncePriceValue)
			? snapshot.bouncePriceValue ?? null
			: row.bouncePriceValue,
		bouncePriceCurrency: shouldUseSnapshotTextFallback(row.bouncePriceCurrency)
			? snapshot.bouncePriceCurrency || ""
			: row.bouncePriceCurrency,
	};
}

function getEuroWiseRate(rows: CountryRatesRow[]): number | null {
	const eurRow = rows.find((row) => String(row.code || "").toUpperCase() === "EUR");
	const wiseRate = eurRow?.wiseRate ?? null;
	if (wiseRate === null || !Number.isFinite(wiseRate) || wiseRate === 0) return null;
	return wiseRate;
}

/**
 * Giá khách đề xuất = giá tham chiếu (cột Bounce Price) × hệ số này.
 * Quân chốt 13/10/2026: 0.8 (trước đây 0.9).
 */
const EST_PUB_RATE_MULTIPLIER = 0.8;

function calcEstPubRate(row: CountryRatesRow, euroWiseRate: number | null): EstPubRateValue {
	const bounceValue = row.bouncePriceValue ?? null;
	const bounceCurrency = String(row.bouncePriceCurrency || "").toUpperCase();
	if (bounceCurrency === "NONE") return "NEW";
	if (bounceValue === null || !Number.isFinite(bounceValue)) return null;
	if (bounceCurrency === "USD") return roundTo(bounceValue * EST_PUB_RATE_MULTIPLIER, 2);
	if (bounceCurrency === "EUR") {
		if (euroWiseRate === null || !Number.isFinite(euroWiseRate) || euroWiseRate === 0) {
			return null;
		}
		return roundTo((bounceValue / euroWiseRate) * EST_PUB_RATE_MULTIPLIER, 2);
	}
	const wiseRate = row.wiseRate ?? null;
	if (wiseRate === null || !Number.isFinite(wiseRate) || wiseRate === 0) return null;
	return roundTo((bounceValue / wiseRate) * EST_PUB_RATE_MULTIPLIER, 2);
}

function formatEstPubRate(value: EstPubRateValue) {
	if (value === "NEW") return "NEW";
	return formatNumber(value, 2);
}

function calcSysRateLocal(row: CountryRatesRow, pubRate: number | null): number | null {
	const wiseRate = row.wiseRate ?? null;
	if (wiseRate === null || pubRate === null) return null;
	if (!Number.isFinite(wiseRate) || !Number.isFinite(pubRate)) return null;
	return roundTo(wiseRate * pubRate, 3);
}

function calcComRate(row: CountryRatesRow, sysRateLocal: number | null): number | null {
	const stationCom = row.stationCom ?? null;
	if (stationCom === null || sysRateLocal === null) return null;
	if (!Number.isFinite(stationCom) || !Number.isFinite(sysRateLocal) || sysRateLocal === 0) return null;
	return stationCom / sysRateLocal;
}

function calcEstSttCom(wiseRate: number | null): number | null {
	if (wiseRate === null || !Number.isFinite(wiseRate)) return null;
	return roundTo(wiseRate * DEFAULT_STT_USD_AMOUNT, 0);
}

function calcEstimatedPayoutFromUsd(wiseRate: number | null, payoutUsd: number | null): number | null {
	if (wiseRate === null || payoutUsd === null) return null;
	if (!Number.isFinite(wiseRate) || !Number.isFinite(payoutUsd) || payoutUsd <= 0) return null;
	return roundTo(wiseRate * payoutUsd, 0);
}

function calcEstMinRupCap(row: CountryRatesRow, minimumPayoutUsd: number | null): number | null {
	return calcEstimatedPayoutFromUsd(row.wiseRate ?? null, minimumPayoutUsd);
}

function calcEstMaxRupCap(row: CountryRatesRow, maxPayoutUsd: number | null): number | null {
	return calcEstimatedPayoutFromUsd(row.wiseRate ?? null, maxPayoutUsd);
}

function normalizeRows(rawRows: any[]): CountryRatesRow[] {
	if (!Array.isArray(rawRows)) return [];
	const normalizedRows = rawRows.map((r: any) => {
		const country = r.country ?? r.Country ?? r["Country"] ?? "";
		const code = r.code ?? r.Code ?? r["Code"] ?? "";
		const currency = r.currency ?? r.Currency ?? r["Currency"] ?? "";
		const wiseRate = parseNumberLoose(r.wiseRate ?? r["Wise Rate"] ?? r.WiseRate);
		const pubRate = parseNumberLoose(
			r.pubRate ??
				r["Pub.Rate (USD/EUR)"] ??
				r["Pub.Rate (USD)"] ??
				r["Pub.Rate (EUR)"] ??
				r["Pub Rate"] ??
				r.PubRate
		);
		const bouncePriceValue = parseNumberLoose(r.bouncePriceValue ?? r["Bounce Price"] ?? r["Bounce Price Value"]);
		const bouncePriceCurrency = r.bouncePriceCurrency ?? r["Bounce Currency"] ?? r["Bounce Price Currency"];
		const stationCom = parseNumberLoose(r.stationCom ?? r["Station Com"] ?? r["Station Commission"]);
		const minimumPayout = parseNumberLoose(r.minimumPayout ?? r["Minimum Payout"] ?? r["Min Payout"]);
		const minPayingCap = parseNumberLoose(r.minPayingCap ?? r["Min Paying Cap"]);
		const maxPayingCap = parseNumberLoose(r.maxPayingCap ?? r["Max Paying Cap"]);

		return applyCountryRateSnapshotFallback({
			country: toCanonicalCountryName(country),
			code: String(code || ""),
			currency: String(currency || ""),
			wiseRate,
			pubRate,
			bouncePriceValue,
			bouncePriceCurrency: String(bouncePriceCurrency || ""),
			stationCom,
			minimumPayout,
			estMinRupCap: null,
			minPayingCap,
			estMaxRupCap: null,
			maxPayingCap,
		});
	});
	return normalizedRows;
}

export default function CountryRates() {
	const theme = useTheme();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [payload, setPayload] = useState<CountryRatesPayload>({ columns: [], rows: [] });
	const [canonicalCountries, setCanonicalCountries] = useState<string[]>([]);
	const [systemPayouts, setSystemPayouts] = useState({
		minimumPayoutUsd: 0,
		maxPayoutUsd: 0,
	});

	const allCurrencyCodes = useMemo(() => {
		const codes = getAllCurrencyCodes().map((c) => String(c || "").toUpperCase());
		codes.sort((a, b) => a.localeCompare(b));
		return uniqUpper(codes);
	}, []);

	const currencyOptionLabels = useMemo(() => {
		// Build labels once so Autocomplete and MenuItems are cheap.
		const map = new Map<string, string>();
		for (const code of allCurrencyCodes) {
			const name = getCurrencyDisplayName(code);
			map.set(code, name ? `${code} — ${name}` : code);
		}
		return map;
	}, [allCurrencyCodes]);

	const currencyOptions = useMemo<CurrencyOption[]>(() => {
		return allCurrencyCodes.map((code) => {
			const name = getCurrencyDisplayName(code) || code;
			const label = currencyOptionLabels.get(code) || code;
			return {
				code,
				name,
				label,
				searchText: `${code} ${name} ${label}`.toLowerCase(),
			};
		});
	}, [allCurrencyCodes, currencyOptionLabels]);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				setLoading(true);
				const [countries, data, systemConfig] = await Promise.all([
					getCountryOptions().catch(() => []),
					getCountryRates(),
					getSystemConfig().catch(() => ({
						minimumPayoutUsd: 0,
						maxPayoutUsd: 0,
					})),
				]);
				if (!mounted) return;
				setCanonicalCountries(Array.isArray(countries) ? countries : []);
				setSystemPayouts({
					minimumPayoutUsd: Number(systemConfig?.minimumPayoutUsd) || 0,
					maxPayoutUsd: Number(systemConfig?.maxPayoutUsd) || 0,
				});
				const normalizedRows = normalizeRows(Array.isArray(data.rows) ? data.rows : []);
				setPayload({
					columns: [],
					rows: normalizedRows,
					updatedAt: data.updatedAt,
				});
			} catch (err: any) {
				alert(
					`Không tải được Country Rates: ${
						err?.response?.data?.message || err?.message || "Vui lòng thử lại"
					}`
				);
			} finally {
				setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	const rows = useMemo(
		() => ((payload.rows || []) as CountryRatesRow[]).map((row) => applyCountryRateSnapshotFallback(row)),
		[payload.rows]
	);
	const euroWiseRate = useMemo(() => getEuroWiseRate(rows), [rows]);

	const countryOptions = useMemo(() => {
		// Backend canonical list first; keep existing row values too so legacy data remains selectable.
		const fromRows = rows.map((r) => r.country ?? "");
		return uniqCountries([...(canonicalCountries || []), ...fromRows]);
	}, [rows, canonicalCountries]);

	const onAddRow = () => {
		setPayload((p) => ({
			...p,
			rows: [
				...(p.rows || []),
				{
					country: "",
					code: "",
					currency: "",
					wiseRate: null,
					pubRate: null,
					bouncePriceValue: null,
					bouncePriceCurrency: "USD",
					stationCom: null,
					minimumPayout: null,
					estMinRupCap: null,
					minPayingCap: null,
					estMaxRupCap: null,
					maxPayingCap: null,
				},
			],
		}));
	};

	const onDeleteRow = (rowIndex: number) => {
		setPayload((p) => ({
			...p,
			rows: (p.rows || []).filter((_, idx) => idx !== rowIndex),
		}));
	};

	const onRowChange = (rowIndex: number, patch: Partial<CountryRatesRow>) => {
		setPayload((p) => {
			const nextRows = [...(p.rows || [])] as CountryRatesRow[];
			const nextPatch = { ...patch };
			if (Object.prototype.hasOwnProperty.call(nextPatch, "country")) {
				nextPatch.country = toCanonicalCountryName(nextPatch.country ?? "");
			}
			nextRows[rowIndex] = { ...(nextRows[rowIndex] || {}), ...nextPatch };
			return { ...p, rows: nextRows };
		});
	};

	const onSave = async () => {
		try {
			setSaving(true);
			const cleanedRows = (rows as any[]).map((r) => {
				const { estMinRupCap, estMaxRupCap, ...rest } = r || {};
				return {
					...rest,
					country: toCanonicalCountryName(rest.country ?? ""),
				};
			});
			await saveCountryRates({
				columns: [],
				rows: cleanedRows,
			});
			alert("Đã lưu Country Rates thành công.");
		} catch (err: any) {
			alert(`Lưu thất bại: ${err?.response?.data?.message || err?.message || "Vui lòng thử lại"}`);
		} finally {
			setSaving(false);
		}
	};

	// ── Excel column name mapping ──────────────────────────────────────
	const EXCEL_COL_MAP: Record<string, keyof CountryRatesRow> = {
		// Support various Excel header names → internal field
		country: "country",
		"quốc gia": "country",
		code: "code",
		mã: "code",
		currency: "currency",
		"tiền tệ": "currency",
		"wise rate": "wiseRate",
		wiserate: "wiseRate",
		"tỷ giá wise": "wiseRate",
		"pub.rate (usd/eur)": "pubRate",
		"pub.rate (usd)": "pubRate",
		"pub.rate (eur)": "pubRate",
		"pub rate": "pubRate",
		"pub.rate": "pubRate",
		pubrate: "pubRate",
		"bounce price": "bouncePriceValue",
		"bounce price value": "bouncePriceValue",
		"bounce price currency": "bouncePriceCurrency",
		"bounce currency": "bouncePriceCurrency",
		"station com": "stationCom",
		"station commission": "stationCom",
		stationcom: "stationCom",
		"minimum payout": "minimumPayout",
		"min payout": "minimumPayout",
		minimumpayout: "minimumPayout",
		"min paying cap": "minPayingCap",
		minpayingcap: "minPayingCap",
		"max paying cap": "maxPayingCap",
		maxpayingcap: "maxPayingCap",
		// Google Sheet duplicate "Currency" column → XLSX renames to Currency_1, Currency_2 etc.
		currency_1: "bouncePriceCurrency",
		currency_2: "bouncePriceCurrency",
		// Estimated caps from Google Sheet (read-only in UI but importable)
		"est min r.up cap": "estMinRupCap",
		"est min rup cap": "estMinRupCap",
		estminrupcap: "estMinRupCap",
		"est max r.up cap": "estMaxRupCap",
		"est max rup cap": "estMaxRupCap",
		estmaxrupcap: "estMaxRupCap",
	};

	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleImportExcel = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;
			const reader = new FileReader();
			reader.onload = (evt) => {
				try {
					const data = new Uint8Array(evt.target?.result as ArrayBuffer);
					const wb = XLSX.read(data, { type: "array" });
					const wsName = wb.SheetNames[0];
					if (!wsName) {
						alert("File Excel không có sheet nào.");
						return;
					}
					const ws = wb.Sheets[wsName];

					// ── Auto-detect header row ──
					// Google Sheets often has a title/date in row 1, with actual headers in row 2+.
					// We scan each row (up to 10) to find one where at least 3 cells match known column names.
					const allRawRows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }) as any[][];
					if (!allRawRows.length) {
						alert("Sheet trống, không có dữ liệu.");
						return;
					}

					let headerRowIdx = -1;
					let detectedHeaders: string[] = [];
					const maxScanRows = Math.min(10, allRawRows.length);
					for (let r = 0; r < maxScanRows; r++) {
						const row = allRawRows[r];
						if (!Array.isArray(row)) continue;
						const cellTexts = row.map((c: any) =>
							String(c ?? "")
								.trim()
								.toLowerCase()
						);
						let matchCount = 0;
						for (const cellText of cellTexts) {
							if (cellText && EXCEL_COL_MAP[cellText]) matchCount++;
						}
						if (matchCount >= 3) {
							headerRowIdx = r;
							detectedHeaders = row.map((c: any) => String(c ?? "").trim());
							break;
						}
					}

					if (headerRowIdx < 0) {
						alert(
							"Không nhận diện được hàng tiêu đề trong file Excel.\n\n" +
								"Hệ thống đã quét 10 hàng đầu tiên nhưng không tìm thấy hàng nào có ≥3 cột khớp.\n\n" +
								"Các cột được hỗ trợ: Country, Code, Currency, Wise Rate, Pub.Rate (USD/EUR), " +
								"Bounce Price, Bounce Price Currency, Station Com, Min Payout, " +
								"Min Paying Cap, Max Paying Cap"
						);
						return;
					}

					// Build data rows from rows after the header
					// Handle duplicate column names (e.g. two "Currency" columns) by appending _1, _2 etc.
					const uniqueHeaders: string[] = [];
					const headerCount: Record<string, number> = {};
					for (const h of detectedHeaders) {
						const key = h.toLowerCase();
						if (headerCount[key] !== undefined) {
							headerCount[key]++;
							uniqueHeaders.push(`${h}_${headerCount[key]}`);
						} else {
							headerCount[key] = 0;
							uniqueHeaders.push(h);
						}
					}

					const dataRows = allRawRows.slice(headerRowIdx + 1);
					const jsonRows: any[] = dataRows
						.filter(
							(row) =>
								Array.isArray(row) &&
								row.some((c: any) => c !== null && c !== undefined && String(c).trim() !== "")
						)
						.map((row) => {
							const obj: any = {};
							for (let i = 0; i < uniqueHeaders.length; i++) {
								const headerName = uniqueHeaders[i];
								if (headerName) obj[headerName] = row[i] ?? null;
							}
							return obj;
						});

					if (!jsonRows.length) {
						alert("Không có dữ liệu sau hàng tiêu đề.");
						return;
					}

					// Map Excel columns to internal fields
					const excelHeaders = Object.keys(jsonRows[0]);
					const colMapping: Record<string, keyof CountryRatesRow> = {};
					for (const header of excelHeaders) {
						const normalized = header.trim().toLowerCase();
						if (EXCEL_COL_MAP[normalized]) {
							colMapping[header] = EXCEL_COL_MAP[normalized];
						}
					}

					if (!Object.keys(colMapping).length) {
						alert(
							"Không nhận diện được cột nào trong file Excel.\n\n" +
								"Các cột được hỗ trợ: Country, Code, Currency, Wise Rate, Pub.Rate (USD/EUR), " +
								"Bounce Price, Bounce Price Currency, Station Com, Min Payout, " +
								"Min Paying Cap, Max Paying Cap"
						);
						return;
					}

					const numFields = new Set<keyof CountryRatesRow>([
						"wiseRate",
						"pubRate",
						"bouncePriceValue",
						"stationCom",
						"minimumPayout",
						"minPayingCap",
						"maxPayingCap",
						"estMinRupCap",
						"estMaxRupCap",
					]);

					const importedRows: CountryRatesRow[] = jsonRows.map((excelRow) => {
						const row: any = {
							country: "",
							code: "",
							currency: "",
							wiseRate: null,
							pubRate: null,
							bouncePriceValue: null,
							bouncePriceCurrency: "",
							stationCom: null,
							minimumPayout: null,
							estMinRupCap: null,
							minPayingCap: null,
							estMaxRupCap: null,
							maxPayingCap: null,
						};
						for (const [excelHeader, field] of Object.entries(colMapping)) {
							const raw = excelRow[excelHeader];
							if (raw === null || raw === undefined || raw === "") continue;
							if (numFields.has(field)) {
								row[field] = parseNumberLoose(raw);
							} else {
								row[field] = field === "country" ? toCanonicalCountryName(raw) : String(raw).trim();
							}
						}
						return row;
					});

					// Merge: update existing countries, append new ones
					const existingRows = [...rows] as CountryRatesRow[];
					const existingMap = new Map<string, number>();
					existingRows.forEach((r, idx) => {
						const key = normalizeCountryKey(r.country || "");
						if (key) existingMap.set(key, idx);
					});

					let updatedCount = 0;
					let addedCount = 0;
					const mergedRows = [...existingRows];

					for (const importedRow of importedRows) {
						const key = normalizeCountryKey(importedRow.country || "");
						if (!key) {
							// No country → append as new row
							mergedRows.push(importedRow);
							addedCount++;
							continue;
						}
						const existingIdx = existingMap.get(key);
						if (existingIdx !== undefined) {
							// Merge: only overwrite fields that have values in Excel
							const merged = { ...mergedRows[existingIdx] };
							for (const [field, val] of Object.entries(importedRow)) {
								if (val !== null && val !== undefined && val !== "") {
									(merged as any)[field] = val;
								}
							}
							mergedRows[existingIdx] = merged;
							updatedCount++;
						} else {
							mergedRows.push(importedRow);
							existingMap.set(key, mergedRows.length - 1);
							addedCount++;
						}
					}

					setPayload((p) => ({ ...p, rows: mergedRows }));

					const mappedCols = Object.entries(colMapping)
						.map(([excel, internal]) => `"${excel}" → ${internal}`)
						.join(", ");
					alert(
						`Import thành công!\n\n` +
							`• ${jsonRows.length} dòng từ Excel\n` +
							`• ${updatedCount} dòng cập nhật (country trùng)\n` +
							`• ${addedCount} dòng thêm mới\n\n` +
							`Các cột đã nhận diện: ${mappedCols}\n\n` +
							`⚠️ Chưa lưu — bấm Save để lưu vào hệ thống.`
					);
				} catch (err: any) {
					console.error("Excel import error:", err);
					alert(`Lỗi đọc file Excel: ${err?.message || "Định dạng không hợp lệ"}`);
				}
			};
			reader.readAsArrayBuffer(file);
			// Reset input value so same file can be re-imported
			e.target.value = "";
		},
		[rows, EXCEL_COL_MAP]
	);

	const handleExportExcel = useCallback(() => {
		const exportRows = rows.map((row, idx) => {
			const estPubRate = calcEstPubRate(row, getEuroWiseRate(rows));
			const pubRate = row.pubRate ?? null;
			const sysRateLocal = calcSysRateLocal(row, pubRate);
			const estSttCom = calcEstSttCom(row.wiseRate ?? null);
			const comRate = calcComRate(row, sysRateLocal);
			return {
				No: idx + 1,
				Country: row.country || "",
				Code: row.code || "",
				Currency: row.currency || "",
				"Wise Rate": row.wiseRate ?? "",
				"Est Pub.Rate": estPubRate ?? "",
				"Pub.Rate (USD/EUR)": row.pubRate ?? "",
				"Sys.Rate (local)": sysRateLocal ?? "",
				"Bounce Price": row.bouncePriceValue ?? "",
				"Bounce Price Currency": row.bouncePriceCurrency || "",
				"Est STT Com": estSttCom ?? "",
				"Station Com": row.stationCom ?? "",
				"Com Rate": comRate != null ? (comRate * 100).toFixed(2) + "%" : "",
				"Min Payout": row.minimumPayout ?? "",
				"Est Min R.up Cap": calcEstMinRupCap(row, systemPayouts.minimumPayoutUsd) ?? "",
				"Min Paying Cap": row.minPayingCap ?? "",
				"Est Max R.up Cap": calcEstMaxRupCap(row, systemPayouts.maxPayoutUsd) ?? "",
				"Max Paying Cap": row.maxPayingCap ?? "",
			};
		});
		const ws = XLSX.utils.json_to_sheet(exportRows);
		// Auto-fit column widths
		const colWidths = Object.keys(exportRows[0] || {}).map((key) => {
			const maxLen = Math.max(key.length, ...exportRows.map((r) => String((r as any)[key] ?? "").length));
			return { wch: Math.min(maxLen + 2, 30) };
		});
		ws["!cols"] = colWidths;
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Country Rates");
		XLSX.writeFile(wb, `Country_Rates_${new Date().toISOString().slice(0, 10)}.xlsx`);
	}, [rows, systemPayouts.maxPayoutUsd, systemPayouts.minimumPayoutUsd]);

	return (
		<MyBookingWrapper>
			<BookingContentWrap style={{ width: "100%", maxWidth: "100%", padding: "24px 24px 40px" }}>
				<BookingContentName>Cấu hình Country Rates</BookingContentName>

				{/* ── Toolbar ── */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 8,
						marginBottom: 16,
						padding: "12px 16px",
						background: "#F9FAFB",
						borderRadius: 10,
						border: "1px solid #E5E7EB",
					}}>
					<div style={{ color: "#6B7280", fontSize: 12, lineHeight: 1.5, flex: 1, minWidth: 300 }}>
						💡 Định dạng số: <b>,</b> phân ngàn — <b>.</b> thập phân (VD: 26,269.000). &nbsp;|&nbsp; Com
						Rate ngoài khoảng <b>35–40%</b> sẽ hiển thị{" "}
						<b style={{ color: theme.palette.error.main }}>đỏ</b>.
					</div>
					<div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
						<PermissionGate permission="settings.manage">
							<button
								onClick={onAddRow}
								disabled={loading || saving}
								style={{
									padding: "6px 14px",
									border: "1px solid #D1D5DB",
									borderRadius: 6,
									background: "#fff",
									color: "#374151",
									fontWeight: 500,
									fontSize: 13,
									cursor: loading || saving ? "not-allowed" : "pointer",
									opacity: loading || saving ? 0.5 : 1,
								}}>
								+ Add Row
							</button>
						</PermissionGate>
						<PermissionGate permission="settings.manage">
							<button
								onClick={onSave}
								disabled={loading || saving}
								style={{
									padding: "6px 14px",
									border: "none",
									borderRadius: 6,
									background: "#2563EB",
									color: "#fff",
									fontWeight: 500,
									fontSize: 13,
									cursor: loading || saving ? "not-allowed" : "pointer",
									opacity: loading || saving ? 0.5 : 1,
								}}>
								{saving ? "Saving..." : "💾 Save"}
							</button>
						</PermissionGate>
						<button
							onClick={() => fileInputRef.current?.click()}
							disabled={loading || saving}
							style={{
								padding: "6px 14px",
								border: "1px solid #93C5FD",
								borderRadius: 6,
								background: "#EFF6FF",
								color: "#1D4ED8",
								fontWeight: 500,
								fontSize: 13,
								cursor: loading || saving ? "not-allowed" : "pointer",
								opacity: loading || saving ? 0.5 : 1,
							}}>
							📥 Import
						</button>
						<button
							onClick={handleExportExcel}
							disabled={loading || saving || rows.length === 0}
							style={{
								padding: "6px 14px",
								border: "1px solid #86EFAC",
								borderRadius: 6,
								background: "#F0FDF4",
								color: "#166534",
								fontWeight: 500,
								fontSize: 13,
								cursor: loading || saving || rows.length === 0 ? "not-allowed" : "pointer",
								opacity: loading || saving || rows.length === 0 ? 0.5 : 1,
							}}>
							📤 Export
						</button>
						<input
							ref={fileInputRef}
							type="file"
							accept=".xlsx,.xls,.csv"
							style={{ display: "none" }}
							onChange={handleImportExcel}
						/>
					</div>
				</div>

				{/* ── Table ── */}
				<div
					style={{
						overflowX: "auto",
						overflowY: "auto",
						maxHeight: "calc(100vh - 280px)",
						borderRadius: 10,
						border: "1px solid #E5E7EB",
						boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
					}}>
					<table
						style={{
							width: "100%",
							borderCollapse: "separate",
							borderSpacing: 0,
							fontSize: 12,
							fontFamily: "'Inter','Segoe UI',sans-serif",
						}}>
						{/* ── Grouped Header Row 1: Section labels ── */}
						<thead>
							<tr>
								<th
									colSpan={4}
									style={{
										...thGroupStyle,
										background: "#EFF6FF",
										color: "#1E40AF",
										borderBottom: "2px solid #BFDBFE",
										position: "sticky",
										top: 0,
										zIndex: 4,
									}}>
									🌍 Country Info
								</th>
								<th
									colSpan={4}
									style={{
										...thGroupStyle,
										background: "#F0FDF4",
										color: "#166534",
										borderBottom: "2px solid #BBF7D0",
										position: "sticky",
										top: 0,
										zIndex: 2,
									}}>
									💱 Exchange Rates
								</th>
								<th
									colSpan={3}
									style={{
										...thGroupStyle,
										background: "#FFF7ED",
										color: "#9A3412",
										borderBottom: "2px solid #FED7AA",
										position: "sticky",
										top: 0,
										zIndex: 2,
									}}>
									💰 Pricing
								</th>
								<th
									colSpan={5}
									style={{
										...thGroupStyle,
										background: "#FAF5FF",
										color: "#6B21A8",
										borderBottom: "2px solid #E9D5FF",
										position: "sticky",
										top: 0,
										zIndex: 2,
									}}>
									📊 Commission & Caps
								</th>
								<th
									style={{
										...thGroupStyle,
										background: "#F9FAFB",
										color: "#6B7280",
										borderBottom: "2px solid #E5E7EB",
										position: "sticky",
										top: 0,
										zIndex: 2,
									}}></th>
							</tr>
							{/* ── Header Row 2: Column names ── */}
							<tr>
								{/* Country Info group */}
								<th
									style={{
										...thStyle,
										position: "sticky",
										left: 0,
										zIndex: 5,
										background: "#EFF6FF",
										width: 36,
									}}>
									#
								</th>
								<th
									style={{
										...thStyle,
										position: "sticky",
										left: 36,
										zIndex: 5,
										background: "#EFF6FF",
										minWidth: 140,
									}}>
									Country
								</th>
								<th style={{ ...thStyle, background: "#EFF6FF", width: 60 }}>Code</th>
								<th style={{ ...thStyle, background: "#EFF6FF", width: 80 }}>Currency</th>
								{/* Exchange Rates group */}
								<th style={{ ...thStyle, background: "#F0FDF4", minWidth: 100 }}>Wise Rate</th>
								<th style={{ ...thStyle, background: "#F0FDF4", width: 70 }}>Est Pub</th>
								<th style={{ ...thStyle, background: "#F0FDF4", minWidth: 100 }}>Pub Rate</th>
								<th style={{ ...thStyle, background: "#F0FDF4", width: 80 }}>Sys.Rate</th>
								{/* Pricing group */}
								<th style={{ ...thStyle, background: "#FFF7ED", minWidth: 170 }}>Bounce Price</th>
								<th style={{ ...thStyle, background: "#FFF7ED", width: 70 }}>Est STT</th>
								<th style={{ ...thStyle, background: "#FFF7ED", minWidth: 95 }}>Stn Com</th>
								{/* Commission & Caps group */}
								<th style={{ ...thStyle, background: "#FAF5FF", width: 70 }}>Com Rate</th>
								<th style={{ ...thStyle, background: "#FAF5FF", width: 70 }}>Est Min</th>
								<th style={{ ...thStyle, background: "#FAF5FF", minWidth: 90 }}>Min Cap</th>
								<th style={{ ...thStyle, background: "#FAF5FF", width: 70 }}>Est Max</th>
								<th style={{ ...thStyle, background: "#FAF5FF", minWidth: 90 }}>Max Cap</th>
								{/* Action */}
								<th style={{ ...thStyle, background: "#F9FAFB", width: 56 }}></th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row, rowIndex) => {
								const estPubRate = calcEstPubRate(row, euroWiseRate);
								const pubRate = row.pubRate ?? null;
								const sysRateLocal = calcSysRateLocal(row, pubRate);
								const estSttCom = calcEstSttCom(row.wiseRate ?? null);
								const estMinPayout = calcEstMinRupCap(row, systemPayouts.minimumPayoutUsd);
								const estMaxPayout = calcEstMaxRupCap(row, systemPayouts.maxPayoutUsd);
								const comRate = calcComRate(row, sysRateLocal);
								const localCode = String(row.code || "").toUpperCase();
								const pricingCurrency = getPricingCurrency(localCode);
								const bounceCur =
									String(row.bouncePriceCurrency || "").toUpperCase() || pricingCurrency;
								const isOutOfRange =
									comRate !== null && Number.isFinite(comRate) && (comRate < 0.35 || comRate > 0.4);
								const rowBg = rowIndex % 2 === 0 ? "#fff" : "#F9FAFB";

								return (
									<tr
										key={rowIndex}
										style={{ background: rowBg }}>
										{/* # */}
										<td
											style={{
												...tdStyle,
												position: "sticky",
												left: 0,
												zIndex: 1,
												background: rowBg,
												textAlign: "center",
												fontWeight: 500,
												color: "#9CA3AF",
											}}>
											{rowIndex + 1}
										</td>
										{/* Country */}
										<td
											style={{
												...tdStyle,
												position: "sticky",
												left: 36,
												zIndex: 1,
												background: rowBg,
											}}>
											<Autocomplete
												freeSolo
												options={uniqCountries([row.country, ...countryOptions])}
												value={String(row.country ?? "")}
												onInputChange={(_, nextVal, reason) => {
													if (reason !== "input" && reason !== "clear") return;
													onRowChange(rowIndex, { country: nextVal });
												}}
												onChange={(_, nextVal) =>
													onRowChange(rowIndex, {
														country:
															typeof nextVal === "string"
																? nextVal
																: String(nextVal ?? ""),
													})
												}
												renderInput={(params) => (
													<TextField
														{...params}
														variant="outlined"
														size="small"
														fullWidth
														placeholder="Nhập để tìm country"
														InputProps={{ ...params.InputProps, style: { fontSize: 12 } }}
													/>
												)}
											/>
										</td>
										{/* Code */}
										<td style={tdStyle}>
											<TextField
												value={row.code ?? ""}
												onChange={(e) => {
													const nextCode = e.target.value;
													const matchedCurrency = findCurrencyOptionByInput(
														nextCode,
														currencyOptions
													);
													onRowChange(rowIndex, {
														code: nextCode,
														currency: matchedCurrency?.name || String(row.currency || ""),
														bouncePriceCurrency:
															String(row.bouncePriceCurrency || "") ||
															getPricingCurrency(nextCode),
													});
												}}
												variant="outlined"
												size="small"
												fullWidth
												InputProps={{ style: { fontSize: 12 } }}
											/>
										</td>
										{/* Currency */}
										<td style={tdStyle}>
											<Autocomplete
												freeSolo
												options={currencyOptions}
												value={String(row.currency ?? "")}
												onInputChange={(_, nextVal, reason) => {
													if (reason !== "input" && reason !== "clear") return;
													const matchedCurrency = findCurrencyOptionByInput(
														nextVal,
														currencyOptions
													);
													onRowChange(rowIndex, {
														currency: matchedCurrency?.name || nextVal,
														code: matchedCurrency?.code || String(row.code || ""),
													});
												}}
												onChange={(_, nextVal) => {
													if (!nextVal) return;
													if (typeof nextVal === "string") {
														const matchedCurrency = findCurrencyOptionByInput(
															nextVal,
															currencyOptions
														);
														onRowChange(rowIndex, {
															currency: matchedCurrency?.name || nextVal,
															code: matchedCurrency?.code || String(row.code || ""),
														});
														return;
													}
													onRowChange(rowIndex, {
														currency: nextVal.name,
														code: nextVal.code,
													});
												}}
												filterOptions={(options, state) => {
													const q = String(state.inputValue || "")
														.trim()
														.toLowerCase();
													if (!q) return options.slice(0, 100);
													return options
														.filter((option) => option.searchText.includes(q))
														.slice(0, 100);
												}}
												getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
												renderInput={(params) => (
													<TextField
														{...params}
														variant="outlined"
														size="small"
														fullWidth
														placeholder="Nhập mã hoặc tên tiền tệ"
														InputProps={{ ...params.InputProps, style: { fontSize: 12 } }}
													/>
												)}
											/>
										</td>
										{/* Wise Rate */}
										<td style={tdStyle}>
											<FormattedNumberField
												value={row.wiseRate ?? null}
												decimals={3}
												disabled={loading || saving}
												onValueChange={(v) => onRowChange(rowIndex, { wiseRate: v })}
											/>
										</td>
										{/* Est Pub */}
										<td style={{ ...tdStyle, textAlign: "right", color: "#6B7280" }}>
											{formatEstPubRate(estPubRate)}
										</td>
										{/* Pub Rate */}
										<td style={tdStyle}>
											<FormattedNumberField
												value={row.pubRate ?? null}
												decimals={2}
												disabled={loading || saving}
												onValueChange={(v) => onRowChange(rowIndex, { pubRate: v })}
											/>
										</td>
										{/* Sys.Rate */}
										<td style={{ ...tdStyle, textAlign: "right", color: "#6B7280" }}>
											{formatNumber(sysRateLocal, 3)}
										</td>
										{/* Bounce Price */}
										<td style={tdStyle}>
											<div style={{ display: "flex", gap: 3, alignItems: "center" }}>
												<div style={{ flex: 1 }}>
													<FormattedNumberField
														value={row.bouncePriceValue ?? null}
														decimals={2}
														disabled={loading || saving}
														onValueChange={(v) =>
															onRowChange(rowIndex, { bouncePriceValue: v })
														}
													/>
												</div>
												<TextField
													select
													value={bounceCur}
													onChange={(e) =>
														onRowChange(rowIndex, {
															bouncePriceCurrency: e.target.value as string,
														})
													}
													variant="outlined"
													size="small"
													style={{ width: 72 }}
													InputProps={{ style: { fontSize: 11 } }}>
													{uniqUpper([
														"NONE",
														pricingCurrency,
														localCode,
														...allCurrencyCodes,
													]).map((code) => (
														<MenuItem
															key={code}
															value={code}
															style={{ fontSize: 11 }}>
															{code === "NONE" ? "—" : code}
														</MenuItem>
													))}
												</TextField>
											</div>
										</td>
										{/* Est STT */}
										<td style={{ ...tdStyle, textAlign: "right", color: "#6B7280" }}>
											{formatNumber(estSttCom, 0)}
										</td>
										{/* Station Com */}
										<td style={tdStyle}>
											<FormattedNumberField
												value={row.stationCom ?? null}
												decimals={2}
												disabled={loading || saving}
												onValueChange={(v) => onRowChange(rowIndex, { stationCom: v })}
											/>
										</td>
										{/* Com Rate */}
										<td
											style={{
												...tdStyle,
												textAlign: "right",
												color: isOutOfRange ? theme.palette.error.main : "#374151",
												fontWeight: isOutOfRange ? 700 : 400,
												background: isOutOfRange ? "#FEF2F2" : undefined,
											}}>
											{formatPercent(comRate, 2)}
										</td>
										{/* Est Min R.up */}
										<td style={{ ...tdStyle, textAlign: "right", color: "#6B7280" }}>
											{formatNumber(estMinPayout, 0)}
										</td>
										{/* Min Cap */}
										<td style={tdStyle}>
											<FormattedNumberField
												value={row.minPayingCap ?? null}
												decimals={0}
												disabled={loading || saving}
												onValueChange={(v) => onRowChange(rowIndex, { minPayingCap: v })}
											/>
										</td>
										{/* Est Max R.up */}
										<td style={{ ...tdStyle, textAlign: "right", color: "#6B7280" }}>
											{formatNumber(estMaxPayout, 0)}
										</td>
										{/* Max Cap */}
										<td style={tdStyle}>
											<FormattedNumberField
												value={row.maxPayingCap ?? null}
												decimals={0}
												disabled={loading || saving}
												onValueChange={(v) => onRowChange(rowIndex, { maxPayingCap: v })}
											/>
										</td>
										{/* Delete */}
										<td style={{ ...tdStyle, textAlign: "center" }}>
											<PermissionGate permission="settings.manage">
												<button
													onClick={() => onDeleteRow(rowIndex)}
													disabled={loading || saving}
													title="Delete row"
													style={{
														border: "none",
														background: "none",
														color: "#EF4444",
														cursor: loading || saving ? "not-allowed" : "pointer",
														fontSize: 16,
														padding: "2px 6px",
														borderRadius: 4,
														opacity: loading || saving ? 0.4 : 1,
													}}>
													🗑
												</button>
											</PermissionGate>
										</td>
									</tr>
								);
							})}
							{rows.length === 0 && (
								<tr>
									<td
										colSpan={17}
										style={{
											...tdStyle,
											textAlign: "center",
											color: "#9CA3AF",
											padding: "32px 8px",
										}}>
										Chưa có dòng dữ liệu. Bấm <b>+ Add Row</b> để thêm.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</BookingContentWrap>
		</MyBookingWrapper>
	);
}

/* ── Shared inline styles for the table ── */
const thGroupStyle: React.CSSProperties = {
	padding: "6px 8px",
	fontSize: 11,
	fontWeight: 700,
	textAlign: "center",
	letterSpacing: 0.5,
	textTransform: "uppercase" as const,
	borderRight: "1px solid rgba(0,0,0,0.06)",
};
const thStyle: React.CSSProperties = {
	padding: "6px 6px",
	fontSize: 11,
	fontWeight: 600,
	color: "#374151",
	whiteSpace: "nowrap",
	textAlign: "left",
	borderBottom: "1px solid #E5E7EB",
	borderRight: "1px solid rgba(0,0,0,0.04)",
	position: "sticky",
	top: 28,
	zIndex: 2,
	background: "#fff",
};
const tdStyle: React.CSSProperties = {
	padding: "4px 5px",
	whiteSpace: "nowrap",
	borderBottom: "1px solid #F3F4F6",
	verticalAlign: "middle",
};
